import { Box, Button, ButtonGroup, ButtonProps, Card, CardContent, CardHeader, createTheme, Grid, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import BaseModel from "../models/_BaseModel";
import { useCallback, useEffect, useRef, useState } from "react";
import { ThemeProvider } from "@emotion/react";
import { ButtonGridTheme, GridColorStyles } from "../expanded-theme";
import { deepmerge } from "@mui/utils";
import {
    ExpandMore as MoreVertIcon,
    Check as CheckIcon,
    Block as BlockIcon,
} from "@mui/icons-material";
import moment from "moment";
import { DATE_FORMAT_DISPLAY_DEFAULT, getDecimalDisplay, getIntegerDisplay } from "../utils/Common";
import { GridViewType } from "../utils/Types";

function GridViewEdit<T extends BaseModel>(props: GridViewType<T>) {
    const {
        id_field,
        containerStyle,
        columns,
        data,
        apiUrl,
        apiCaller
    } = props;

    const [rows, setRows] = useState<T[]>([]);
    const [loading, setLoading] = useState(false);
    const [openMenuButton, setOpenMenuButton] = useState(false);
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const selectedDataRef = useRef<T | null>(null);
  
    const handleMenuButtonClick = (event: any, data: T) => {
        if (!columns?.filter(c => c.columnType === "action")[0]?.actions?.filter(a => a.disabled ? !a.disabled(selectedDataRef?.current ?? undefined) : true)?.length)
            return;

        setAnchorEl(event.currentTarget);
        setOpenMenuButton(true);
        selectedDataRef.current = data;
    };
  
    const handleMenuButtonClose = () => {
        selectedDataRef.current = null;
        setOpenMenuButton(false);
    };

    const loadData = useCallback(() => {
        if (!apiUrl || !apiCaller)
            return;

        setLoading(true);

        apiCaller.get(apiUrl)
            .then((response) => {
                setRows([...response.data.data.map((d: any) => {
                    const result : any = {};

                    columns.forEach(c => {
                        if (c.columnType === "action")
                            return;

                        result[c.columnName] = d[c.columnName];
                    });

                    return result;
                })]);
            })
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
    }, [apiUrl, columns]);

    useEffect(() => {
        if (!apiUrl || !apiCaller)
        {
            if (data)
                setRows([...data]);

            return;
        }

        loadData();
    }, [data, apiUrl, apiCaller]);

    return (
        <>
            <Grid container spacing={2} alignItems="flex-start" alignContent="flex-start" justifyContent="flex-start">
            {
                rows.map((row: any, idx) => {
                    const titleColumn = columns.filter(c => c.titleColumn)[0];
                    const actionColumn = columns.filter(c => c.columnType === "action")[0];
                    const buttonProps: ButtonProps = {
                        size: "small",
                        variant: "outlined",
                        color: "white",
                        sx: {
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            boxShadow: "none",
                        },
                    };

                    return (
                        <Grid
                            item
                            xl={3}
                            lg={4}
                            md={6}
                            sm={12}
                            xs={12}
                            key={`data_cell_${idx}`}
                            sx={{marginBottom: "1rem", display: "flex", justifyContent: "center", alignItems: "center"}}
                        >
                            <Card sx={{ backgroundColor: "#fefefe", boxShadow: "0px 2px 10px 2px rgba(0, 0, 0, 0.2)", border: "1px solid #ccc", width: "100%" }}>
                                <CardHeader
                                    title={titleColumn ? (row as any)[titleColumn.columnName] : "-"}
                                    titleTypographyProps={{ fontSize: "0.9rem" }}
                                    sx={{paddingY: "0.5rem", paddingX: "1rem"}}
                                    className="bg-grid-dark text-bg-grid-dark"
                                    action={
                                        <ThemeProvider theme={createTheme(deepmerge(GridColorStyles, ButtonGridTheme))}>
                                        {
                                            (actionColumn?.actions?.length ?? 0) <= 0 ?
                                            <ButtonGroup>
                                            {
                                                actionColumn?.actions?.map((a, a_idx) => (
                                                    <Tooltip
                                                        key={`data_cell_action_${idx}_${a_idx}`}
                                                        title={a.title}
                                                        arrow
                                                    >
                                                        <Button
                                                            {...buttonProps}
                                                            onClick={() => a.action(row)}
                                                            disabled={a.disabled ? a.disabled(row) : false}
                                                        >
                                                        {
                                                            a.icon ? <a.icon fontSize="small" sx={{paddingBottom: 0, fontSize: "0.8rem"}} /> : null
                                                        }
                                                        </Button>
                                                    </Tooltip>
                                                ))
                                            }
                                            </ButtonGroup>
                                            :
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="white"
                                                id="dropdown-button"
                                                aria-controls={openMenuButton ? 'basic-menu' : undefined}
                                                aria-haspopup="true"
                                                aria-expanded={openMenuButton ? 'true' : undefined}
                                                onClick={(e) => handleMenuButtonClick(e, row)}
                                            >
                                                <MoreVertIcon fontSize="small" sx={{paddingBottom: 0, fontSize: "0.8rem"}} />
                                            </Button>
                                        }
                                        </ThemeProvider>
                                    }
                                />
                                <CardContent sx={{paddingY: "0.5rem !important", paddingX: "1rem"}}>
                                    <Typography variant="body2" color="text.secondary">
                                    {
                                        columns?.filter(c => c.columnType === "data" && !c.titleColumn)?.map((dt, dt_idx) => {
                                            return (
                                                <Box
                                                    sx={{display: "flex", flexDirection: "row", justifyContent: "start", alignItems: "start", width: "100%"}}
                                                    key={`data_cell_details_${idx}_${dt_idx}`}
                                                >
                                                    <Grid item xl={4} lg={4} md={5} sm={6} xs={6}><span style={{whiteSpace: "nowrap", display: "inline-block", overflow: "hidden", width: "100%", fontSize: "0.7rem"}}>{dt.columnTitle}</span></Grid>
                                                    <Grid item xl={8} lg={8} md={7} sm={6} xs={6}>
                                                        <span style={{whiteSpace: "nowrap", display: "inline-block", textOverflow: "ellipsis", overflow: "hidden", width: "100%", fontSize: "0.7rem"}}>
                                                            :
                                                            <b>
                                                            {
                                                                (row as any)[dt.columnName] !== null && (row as any)[dt.columnName] !== undefined ?
                                                                (
                                                                    dt.fieldType === "date" || dt.fieldType === "datetime" ?
                                                                    moment((row as any)[dt.columnName]).format(DATE_FORMAT_DISPLAY_DEFAULT)
                                                                    :

                                                                    dt.fieldType === "integer" ?
                                                                    getIntegerDisplay((row as any)[dt.columnName] as number)
                                                                    :

                                                                    dt.fieldType === "float" ?
                                                                    getDecimalDisplay((row as any)[dt.columnName] as number)
                                                                    :

                                                                    dt.fieldType === "boolean" ?
                                                                    (
                                                                        (row as any)[dt.columnName] ? (dt.inverseValue ? <BlockIcon sx={{color: "#d32f2f"}} /> : <CheckIcon color="success" sx={{color: "#2e7d32"}} />) : (dt.inverseValue ? <CheckIcon sx={{color: "#2e7d32"}} /> : <BlockIcon sx={{color: "#d32f2f"}} />)
                                                                    )
                                                                    :

                                                                    (row as any)[dt.columnName]
                                                                )
                                                                :
                                                                "N/A"
                                                            }
                                                            </b>
                                                        </span></Grid>
                                                {
                                                    dt_idx === columns?.length - 1 ? null : <br/>
                                                }
                                                </Box>
                                            );
                                        })
                                    }
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })
            }
            </Grid>
        {
            openMenuButton && selectedDataRef?.current ?
            <Menu
                id="dropdown-menu"
                anchorEl={anchorEl}
                open={openMenuButton}
                onClose={handleMenuButtonClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                    sx: {
                        backgroundColor: "#fff",
                    },
                }}
                sx={{
                    "& > .MuiPaper-root": {
                        boxShadow: "none",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                    },
                }}
            >
            {
                columns?.filter(c => c.columnType === "action")[0]?.actions?.filter(a => a.disabled ? !a.disabled(selectedDataRef?.current ?? undefined) : true)?.map((a, a_idx) => (
                    <MenuItem
                        key={`data_cell_action_${a_idx}`}
                        onClick={() => {
                            a.action(selectedDataRef?.current as T);
                            handleMenuButtonClose();
                        }}
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "start",
                            alignItems: "center",
                            boxShadow: "none",
                            borderRadius: 0,
                            borderRight: "1px solid rgba(255, 255, 255, 0.5)",
                            fontSize: "0.9rem",
                        }}
                        disabled={a.disabled ? a.disabled(selectedDataRef?.current ?? undefined) : false}
                        className="text-bg-grid-normal"
                    >
                    {
                        a.icon ?
                        <Box
                            sx={{
                                border: "1px solid #444",
                                padding: "4px",
                                minWidth: "2.2rem",
                                minHeight: "2.2rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "8px",
                                borderRadius: "0.4rem",
                            }}
                        >
                            <a.icon
                                fontSize="small"
                                sx={{
                                    fontSize: "0.9rem",
                                    // color: "#2196F3",
                                }}
                            />
                        </Box>
                        :
                        null
                    }
                    {
                        a.title
                    }
                    </MenuItem>
                ))
            }
            </Menu>
            :
            null
        }
        </>
    );
}

export default GridViewEdit;
